/**
 * DET ORANSJE BÅNDET (A11, 1031): «Du er inne som support i {organisasjon}», når det utløper, og
 * «Gå ut», som avslutter supportmedlemskapet med en gang og tar plattformadminen tilbake til
 * Control Room.
 *
 * Samme plass som gjestelinja (P10, funn D): over hele skallet, over dialoglaget, så et panel aldri
 * dekker veien ut. Fargen er advarselens (§16.6, ikke pynt): det er et unntak fra det vanlige, og
 * den som står i det, skal ikke glemme det.
 */
import type { MeResponse } from '@tvitvi/shared';
import { useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useSetActiveOrganization } from '@/api/queries/me';
import { Button } from '@/components/ui/Button';
import { ICONS } from '@/components/ui/icons';
import { useToast } from '@/components/ui/Toast';
import { currentLocale } from '@/i18n';
import { cn } from '@/lib/cn';
import { formatDateTime } from '@/lib/format';

import { useLeaveSupport } from './support-queries';

/** Det aktive medlemskapet, når det er et supportmedlemskap. */
export function activeSupport(
  me: MeResponse,
): { organizationId: string; organizationName: string; supportUntil: string } | null {
  const active = me.memberships.find((one) => one.organizationId === me.activeOrganizationId);
  const until = active?.supportUntil;
  if (active === undefined || until === undefined || until === null) return null;
  return {
    organizationId: active.organizationId,
    organizationName: active.organizationName,
    supportUntil: until,
  };
}

export function SupportBar({ me }: { me: MeResponse }): ReactNode {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const leave = useLeaveSupport();
  const switchOrganization = useSetActiveOrganization();
  const support = activeSupport(me);
  if (support === null) return null;

  /* Tilbake til Tvitvi-organisasjonen (Control Room bor der), ellers den første andre. */
  const home =
    me.memberships.find((one) => one.organizationKind === 'platform') ??
    me.memberships.find((one) => one.organizationId !== support.organizationId) ??
    null;
  const Icon = ICONS.warning;

  const goOut = async (): Promise<void> => {
    try {
      /*
       * Bytt først, gå ut etterpå. I motsatt rekkefølge hentet hele bufferen på nytt i kunden etter at
       * medlemskapet var borte, og hver spørring fikk 403 NOT_A_MEMBER (klikkrunde 3).
       */
      if (home !== null) await switchOrganization.mutateAsync(home.organizationId);
      await leave.mutateAsync(support.organizationId);
      toast({ title: t('controlRoom.support.left'), tone: 'success' });
      void navigate({ to: '/control-room' });
    } catch {
      toast({ title: t('controlRoom.support.leaveFailed'), tone: 'error' });
    }
  };

  return (
    <div
      role="region"
      aria-label={t('controlRoom.support.bar.label')}
      data-testid="support-bar"
      style={{ pointerEvents: 'auto' }}
      className={cn(
        'glass-nav relative z-[60] mx-[var(--shell-gap)] mt-[var(--shell-gap)] overflow-hidden',
        'flex min-h-[var(--topbar-height)] shrink-0 items-center justify-between gap-3',
        'rounded-[var(--radius-bar)] px-4 py-1.5',
      )}
    >
      {/* Advarselsfargen over navigasjonsglasset, så båndet leses på hver bakgrunn (§16.4). */}
      <span aria-hidden className="pointer-events-none absolute inset-0 bg-warning-subtle" />
      <p className="relative flex min-w-0 items-center gap-2 text-sm text-default">
        <Icon aria-hidden className="size-4 shrink-0 text-[color:var(--color-warning)]" />
        <span className="truncate">
          <span className="font-semibold">
            {t('controlRoom.support.bar.text', { organization: support.organizationName })}
          </span>{' '}
          <span className="text-muted" data-testid="support-bar-until">
            {t('controlRoom.support.bar.until', {
              time: formatDateTime(support.supportUntil, currentLocale()),
            })}
          </span>
        </span>
      </p>
      <Button
        variant="secondary"
        size="sm"
        className="relative"
        loading={leave.isPending || switchOrganization.isPending}
        onClick={() => void goOut()}
        data-testid="support-bar-leave"
      >
        {t('controlRoom.support.bar.leave')}
      </Button>
    </div>
  );
}
